import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table,
  Input,
  InputNumber,
  Breadcrumb,
  Modal,
  message,
  Button,
  DatePicker,
  Space,
  Grid,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Avatar,
  Tooltip,
  Dropdown,
  Tag,
  Select,
  Descriptions,
  Alert,
  Divider,
  Result,
  Spin,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  DeleteOutlined,
  RightOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  PhoneOutlined,
  DollarOutlined,
  CalendarOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  AlertOutlined,
  RiseOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { transactionsService, citiesService } from '../../common/services';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Can from '../../components/can';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Générer une couleur basée sur le nom
const getAvatarColor = (name) => {
  const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#722ed1', '#eb2f96'];
  const index = (name || '').charCodeAt(0) % colors.length;
  return colors[index];
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  let local = phone.replace('+225', '').trim();
  return '+225 ' + local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

const formatDate = (date, dateStr) => {
  if (dateStr) return dateStr;
  if (!date) return '-';

  const formats = ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY'];
  let parsed = dayjs(date);

  if (!parsed.isValid()) {
    for (const fmt of formats) {
      parsed = dayjs(date, fmt);
      if (parsed.isValid()) break;
    }
  }

  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }

  return typeof date === 'string' ? date : '-';
};

const formatAmount = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('fr-FR').format(amount);
};

// Configuration des tranches de montant
const getAmountConfig = (amount) => {
  if (amount >= 1000) return { color: '#f5222d', bgColor: '#fff2f0', label: 'Élevé' };
  if (amount >= 500) return { color: '#fa8c16', bgColor: '#fff7e6', label: 'Moyen' };
  return { color: '#52c41a', bgColor: '#f6ffed', label: 'Faible' };
};

const HistoriqueSosCredits = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // Statistiques
  const [stats, setStats] = useState({
    totalSos: 0,
    montantTotal: 0,
    montantMoyen: 0,
    today: 0,
    chauffeursUniques: 0,
  });

  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [selectedCity, setSelectedCity] = useState(undefined);
  const [cities, setCities] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-date');

  // Modal SOS Crédit Admin
  const [sosModalOpen, setSosModalOpen] = useState(false);
  const [sosStep, setSosStep] = useState('search'); // 'search' | 'confirm' | 'success'
  const [driverOptions, setDriverOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [foundDriver, setFoundDriver] = useState(null);
  const [sosMontant, setSosMontant] = useState(null);
  const [granting, setGranting] = useState(false);
  const [sosResult, setSosResult] = useState(null);
  const searchTimer = useRef(null);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  const resetSosModal = () => {
    setSosStep('search');
    setDriverOptions([]);
    setFoundDriver(null);
    setSosMontant(null);
    setSosResult(null);
    setSearching(false);
    setGranting(false);
  };

  const handleDriverSearch = (rawValue) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    // Si c'est uniquement des chiffres, limiter à 10 caractères
    let value = rawValue;
    const digitsOnly = rawValue.replace(/\s+/g, '');
    if (/^\d+$/.test(digitsOnly) && digitsOnly.length > 10) {
      value = digitsOnly.slice(0, 10);
    }
    if (!value || value.trim().length < 2) {
      setDriverOptions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        setSearching(true);
        const results = await transactionsService.rechercherChauffeurPourSos(value.trim());
        const options = (Array.isArray(results) ? results : []).map((d) => ({
          value: d.phone,
          driver: d,
          label: (
            <Space>
              <Avatar size="small" style={{ backgroundColor: getAvatarColor(d.firstName) }}>
                {(d.firstName || '?').charAt(0)}{(d.lastName || '?').charAt(0)}
              </Avatar>
              <div style={{ lineHeight: 1.3 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{d.firstName} {d.lastName}</div>
                <div style={{ fontSize: 11, color: '#888' }}>{formatPhoneNumber(d.phone)} — {formatAmount(d.pointsDisponibles)} pts</div>
              </div>
            </Space>
          ),
        }));
        setDriverOptions(options);
      } catch {
        setDriverOptions([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  const handleDriverSelect = (_value, option) => {
    setFoundDriver(option.driver);
    setSosStep('confirm');
  };

  const handleGrantSos = async () => {
    if (!sosMontant || sosMontant <= 0) {
      message.warning("Veuillez saisir un montant valide.");
      return;
    }
    if (!foundDriver) return;

    const totalNeeded = sosMontant + (foundDriver.fraisSos || 50);
    if (totalNeeded > foundDriver.pointsDisponibles) {
      message.error(`Points insuffisants. Il faut ${totalNeeded} points (montant + frais), mais le chauffeur n'a que ${foundDriver.pointsDisponibles} points.`);
      return;
    }

    try {
      setGranting(true);
      const result = await transactionsService.accorderSosCredit({
        phone: foundDriver.phone,
        montant: sosMontant,
      });
      setSosResult(result);
      setSosStep('success');
      fetchList();
    } catch (e) {
      const msg = e?.message || e?.data?.message || "Erreur lors de l'accord du SOS crédit.";
      message.error(msg);
    } finally {
      setGranting(false);
    }
  };

  // Calculer les stats
  const calculateStats = useCallback((data) => {
    const today = dayjs().format('DD/MM/YYYY');
    const todayCount = data.filter(d => {
      const dateStr = d.dateStr || formatDate(d.date, null);
      return dateStr.startsWith(today);
    }).length;

    const montantTotal = data.reduce((sum, d) => sum + (d.amount || 0), 0);
    const chauffeursUniques = new Set(data.map(d => d.phone)).size;

    setStats({
      totalSos: data.length,
      montantTotal,
      montantMoyen: data.length > 0 ? Math.round(montantTotal / data.length) : 0,
      today: todayCount,
      chauffeursUniques,
    });
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      // Supprimer les espaces de la recherche pour les numéros de téléphone
      const cleanedQ = q ? q.replace(/\s+/g, '') : '';
      const params = {
        page, limit, sort,
        q: cleanedQ || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end: dateRange?.[1]?.format('YYYY-MM-DD'),
        cityId: selectedCity || undefined,
      };
      const data = await transactionsService.sosCreditHistoriqueList(params);
      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
        calculateStats(data);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
        // Fetch all for stats
        const allData = await transactionsService.sosCreditHistoriqueList({ ...params, limit: 10000 });
        calculateStats(Array.isArray(allData) ? allData : (allData.items || []));
      }
    } catch (e) {
      console.error("Erreur chargement SOS:", e);
      message.error("Erreur lors du chargement des SOS crédits");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, q, dateRange, selectedCity, calculateStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Charger les villes
  useEffect(() => {
    citiesService.getAll().then((data) => {
      const list = Array.isArray(data) ? data : (data?.items || data?.data || []);
      setCities(list.filter(c => c.isActive !== false));
    }).catch(() => {});
  }, []);

  const handleDelete = (row) => {
    Modal.confirm({
      title: (
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          <span>Confirmer la suppression</span>
        </Space>
      ),
      content: (
        <Card size="small" style={{ marginTop: 16, backgroundColor: '#fff2f0' }}>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Text type="secondary">Chauffeur :</Text>
              <br />
              <Text strong>{row.fullName || 'Inconnu'}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Téléphone :</Text>
              <br />
              <Text>{formatPhoneNumber(row.phone)}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Montant :</Text>
              <br />
              <Tag color="red">{formatAmount(row.amount)} F CFA</Tag>
            </Col>
            <Col span={24}>
              <Text type="secondary">Date :</Text>
              <br />
              <Text>{formatDate(row.date, row.dateStr)}</Text>
            </Col>
          </Row>
        </Card>
      ),
      okText: "Oui, supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      width: isMobile ? '95%' : 480,
      onOk: async () => {
        try {
          await transactionsService.deleteHistoriqueSosCreditById(row._id);
          message.success("SOS crédit supprimé avec succès !");
          fetchList();
        } catch (e) {
          console.error(e);
          message.error("Erreur lors de la suppression");
        }
      }
    });
  };

  // Export
  const fetchAllForExport = async () => {
    const cleanedQ = q ? q.replace(/\s+/g, '') : '';
    const params = {
      page: 1, limit: 5000, sort,
      q: cleanedQ || undefined,
      start: dateRange?.[0]?.format('YYYY-MM-DD'),
      end: dateRange?.[1]?.format('YYYY-MM-DD'),
      cityId: selectedCity || undefined,
    };
    const data = await transactionsService.sosCreditHistoriqueList(params);
    return Array.isArray(data) ? data : (data.items || []);
  };

  const exportExcel = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Chauffeur: it.fullName || '',
        Téléphone: formatPhoneNumber(it.phone || ''),
        Ville: it.cityName || '',
        'Montant (F CFA)': it.amount ?? 0,
        Date: formatDate(it.date, it.dateStr),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'SOS_Credit');
      XLSX.writeFile(wb, 'sos_credits.xlsx');
      message.success({ content: 'Export Excel réussi!', key: 'export' });
    } catch {
      message.error({ content: 'Export Excel: une erreur est survenue.', key: 'export' });
    }
  };

  const exportCSV = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Chauffeur: it.fullName || '',
        Telephone: formatPhoneNumber(it.phone || ''),
        Ville: it.cityName || '',
        Montant: it.amount ?? 0,
        Date: formatDate(it.date, it.dateStr),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'sos_credits.csv');
      message.success({ content: 'Export CSV réussi!', key: 'export' });
    } catch {
      message.error({ content: 'Export CSV: une erreur est survenue.', key: 'export' });
    }
  };

  const exportPDF = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const doc = new jsPDF();
      doc.text('Historique SOS Crédit', 14, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Chauffeur', 'Téléphone', 'Ville', 'Montant', 'Date']],
        body: all.map(it => [
          it.fullName || '',
          formatPhoneNumber(it.phone || ''),
          it.cityName || '',
          `${formatAmount(it.amount)} F`,
          formatDate(it.date, it.dateStr)
        ]),
      });
      doc.save('sos_credits.pdf');
      message.success({ content: 'Export PDF réussi!', key: 'export' });
    } catch {
      message.error({ content: 'Export PDF: une erreur est survenue.', key: 'export' });
    }
  };

  const exportMenuItems = [
    { key: 'excel', icon: <FileExcelOutlined style={{ color: '#52c41a' }} />, label: 'Excel (.xlsx)', onClick: exportExcel },
    { key: 'csv', icon: <DownloadOutlined style={{ color: '#1890ff' }} />, label: 'CSV', onClick: exportCSV },
    { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />, label: 'PDF', onClick: exportPDF },
  ];

  const columns = [
    {
      title: 'Chauffeur',
      key: 'chauffeur',
      width: 250,
      render: (_, record) => {
        const name = record.fullName || 'Inconnu';
        const initials = name.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase();

        return (
          <Space>
            <Avatar style={{ backgroundColor: getAvatarColor(name) }} size={40}>
              {initials}
            </Avatar>
            <div>
              <Text strong style={{ display: 'block', fontSize: 13 }}>{name}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {formatPhoneNumber(record.phone)}
              </Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      sorter: true,
      render: (amount) => {
        const config = getAmountConfig(amount);
        return (
          <Tag
            style={{
              backgroundColor: config.bgColor,
              borderColor: config.color,
              color: config.color,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {formatAmount(amount)} F CFA
          </Tag>
        );
      },
    },
    {
      title: 'Ville',
      dataIndex: 'cityName',
      key: 'cityName',
      width: 120,
      render: (city) => city ? <Tag color="geekblue">{city}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 140,
      sorter: true,
      render: (d, row) => {
        const dateStr = formatDate(d, row.dateStr);
        const parts = dateStr.split(' ');

        return (
          <Tooltip title={dateStr}>
            <Space direction="vertical" size={0}>
              <Text style={{ fontSize: 12 }}>{parts[0] || dateStr}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{parts[1] || ''}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Can perm="soscredit.history:delete">
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Can>
      ),
    },
  ];

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);
    if (sorter?.field || sorter?.columnKey) {
      const key = sorter.columnKey || sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';
      if (['date', 'amount', 'fullName', 'phone'].includes(key)) {
        setSort(`${dir}${key}`);
      }
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: 'Transactions' },
          { title: 'SOS Crédits' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} md={4}>
          <Card
            size="small"
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
              border: 'none'
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total SOS</span>}
              value={stats.totalSos}
              valueStyle={{ color: '#fff', fontSize: isMobile ? 20 : 24 }}
              prefix={<AlertOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card
            size="small"
            style={{ background: '#fff2f0', borderColor: '#ff4d4f' }}
          >
            <Statistic
              title={<span style={{ color: '#ff4d4f' }}>Montant total</span>}
              value={stats.montantTotal}
              valueStyle={{ color: '#ff4d4f', fontSize: isMobile ? 18 : 22 }}
              suffix="F"
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={5}>
          <Card
            size="small"
            style={{ background: '#fff7e6', borderColor: '#fa8c16' }}
          >
            <Statistic
              title={<span style={{ color: '#fa8c16' }}>Montant moyen</span>}
              value={stats.montantMoyen}
              valueStyle={{ color: '#fa8c16', fontSize: isMobile ? 18 : 22 }}
              suffix="F"
              prefix={<RiseOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={5}>
          <Card
            size="small"
            style={{ background: '#e6f7ff', borderColor: '#1890ff' }}
          >
            <Statistic
              title={<span style={{ color: '#1890ff' }}>Chauffeurs uniques</span>}
              value={stats.chauffeursUniques}
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? 20 : 24 }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Card
            size="small"
            style={{ background: '#f6ffed', borderColor: '#52c41a' }}
          >
            <Statistic
              title={<span style={{ color: '#52c41a' }}>Aujourd'hui</span>}
              value={stats.today}
              valueStyle={{ color: '#52c41a', fontSize: isMobile ? 20 : 24 }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres et tableau */}
      <Card>
        {/* Filtres */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={7}>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Rechercher par nom, téléphone..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              allowClear
              size={isMobile ? 'middle' : 'large'}
            />
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Select
              placeholder="Ville"
              value={selectedCity}
              onChange={(v) => { setSelectedCity(v); setPage(1); }}
              allowClear
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
            >
              {cities.map((c) => (
                <Select.Option key={c._id} value={c._id}>{c.name}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={8} md={5}>
            <RangePicker
              onChange={(r) => { setDateRange(r); setPage(1); }}
              placeholder={['Date début', 'Date fin']}
              format="DD/MM/YYYY"
              allowClear
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={12} sm={4} md={4}>
            <Dropdown
              menu={{ items: exportMenuItems }}
              trigger={['click']}
            >
              <Button
                icon={<DownloadOutlined />}
                size={isMobile ? 'middle' : 'large'}
                style={{ width: '100%' }}
              >
                Exporter <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
              </Button>
            </Dropdown>
          </Col>
          <Col xs={12} sm={6} md={5}>
            <Can perm="soscredit:grant">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size={isMobile ? 'middle' : 'large'}
                style={{ width: '100%', background: '#fa8c16', borderColor: '#fa8c16' }}
                onClick={() => { resetSosModal(); setSosModalOpen(true); }}
              >
                Accorder un crédit
              </Button>
            </Can>
          </Col>
        </Row>

        {/* Résultats */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            {total} SOS crédit{total > 1 ? 's' : ''} trouvé{total > 1 ? 's' : ''}
          </Text>
        </div>

        {/* Tableau */}
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          onChange={onTableChange}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 700 }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50, 100],
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
          }}
          rowClassName={(_, index) =>
            index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
          }
        />
      </Card>

      {/* Modal Accorder SOS Crédit */}
      <Modal
        title={
          <Space>
            <ThunderboltOutlined style={{ color: '#fa8c16' }} />
            <span>Accorder un SOS Crédit</span>
          </Space>
        }
        open={sosModalOpen}
        onCancel={() => setSosModalOpen(false)}
        footer={null}
        width={isMobile ? '95%' : 520}
        destroyOnClose
      >
        {/* Étape 1 : Recherche du chauffeur par autocomplétion */}
        {sosStep === 'search' && (
          <div>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Recherchez le chauffeur par nom ou numéro de téléphone.
            </Typography.Paragraph>
            <Select
              showSearch
              size="large"
              style={{ width: '100%' }}
              placeholder="Tapez un nom ou numéro (ex: Coulibaly, 0701...)"
              filterOption={false}
              onSearch={handleDriverSearch}
              onSelect={handleDriverSelect}
              onInputKeyDown={(e) => {
                const input = e.target;
                const currentVal = (input.value || '').replace(/\s+/g, '');
                // Si que des chiffres et déjà 10, bloquer la saisie de nouveaux chiffres
                if (/^\d+$/.test(currentVal) && currentVal.length >= 10 && /\d/.test(e.key) && !e.ctrlKey && !e.metaKey) {
                  e.preventDefault();
                }
              }}
              options={driverOptions}
              notFoundContent={searching ? <Spin size="small" /> : (driverOptions.length === 0 ? <Typography.Text type="secondary">Tapez au moins 2 caractères...</Typography.Text> : null)}
              suffixIcon={<SearchOutlined />}
              autoFocus
            />
          </div>
        )}

        {/* Étape 2 : Confirmation et saisie du montant */}
        {sosStep === 'confirm' && foundDriver && (
          <div>
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Chauffeur">
                  <Typography.Text strong>{foundDriver.firstName} {foundDriver.lastName}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label="Téléphone">
                  {formatPhoneNumber(foundDriver.phone)}
                </Descriptions.Item>
                <Descriptions.Item label="Points disponibles">
                  <Tag color="blue" style={{ fontSize: 14 }}>{formatAmount(foundDriver.pointsDisponibles)} pts</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Coefficient">
                  {foundDriver.coefficient}
                </Descriptions.Item>
                <Descriptions.Item label="Frais SOS">
                  {foundDriver.fraisSos} pts
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Divider style={{ margin: '12px 0' }} />

            <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
              Montant en points :
            </Typography.Text>
            <InputNumber
              style={{ width: '100%' }}
              size="large"
              min={1}
              max={foundDriver.pointsDisponibles - (foundDriver.fraisSos || 50)}
              placeholder="Saisir le montant en points"
              value={sosMontant}
              onChange={(v) => setSosMontant(v)}
              addonAfter="pts"
            />

            {sosMontant > 0 && (
              <Alert
                style={{ marginTop: 12 }}
                type="info"
                showIcon
                message={
                  <span>
                    <strong>{sosMontant} pts</strong> + <strong>{foundDriver.fraisSos} pts</strong> de frais = <strong>{sosMontant + foundDriver.fraisSos} pts</strong> déduits
                    {' '}&rarr;{' '}
                    <Tag color="green">{formatAmount(Math.round(sosMontant * foundDriver.coefficient))} F CFA</Tag> crédités
                  </span>
                }
              />
            )}

            <Row gutter={12} style={{ marginTop: 20 }}>
              <Col span={12}>
                <Button
                  block
                  size="large"
                  onClick={() => { setSosStep('search'); setFoundDriver(null); setSosMontant(null); setDriverOptions([]); }}
                >
                  Retour
                </Button>
              </Col>
              <Col span={12}>
                <Button
                  block
                  type="primary"
                  size="large"
                  icon={<ThunderboltOutlined />}
                  loading={granting}
                  disabled={!sosMontant || sosMontant <= 0}
                  onClick={handleGrantSos}
                  style={{ background: '#fa8c16', borderColor: '#fa8c16' }}
                >
                  Accorder
                </Button>
              </Col>
            </Row>
          </div>
        )}

        {/* Étape 3 : Succès */}
        {sosStep === 'success' && (
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="SOS Crédit accordé !"
            subTitle={sosResult?.message}
            extra={
              <Button type="primary" onClick={() => setSosModalOpen(false)}>
                Fermer
              </Button>
            }
          />
        )}
      </Modal>

      {/* Styles */}
      <style>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #fafafa;
        }
        .table-row-light:hover td,
        .table-row-dark:hover td {
          background-color: #fff2f0 !important;
        }
      `}</style>
    </>
  );
};

export default HistoriqueSosCredits;
